import {
    Input,
    Component,
    ContentChild,
    TemplateRef,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { List as ImmutableList } from 'immutable';
import {
    enterKey,
    arrowUpKey,
    arrowDownKey
} from '../../constants-types/constants';
import { List } from '../../constants-types/PiList.model';
import { TranslationService } from '../../translations';

export interface TypeaheadItem {
    text: string;
}

@Component({
    selector: 'ok-typeahead',
    host: {
        class: 'ok-typeahead'
    },
    template: `
        <div
            [ngClass]="{
                'input-icon input-icon-search': searchStyling,
                'input-icon-right color-primary': isColorPickerEnabled
            }"
        >
            <input
                #inputControl
                class="form-control"
                autocomplete="ChromePlsGo"
                (focus)="focusGained()"
                (blur)="focusLost()"
                (keydown)="keyPressed($event)"
                [(ngModel)]="boundValue"
                [attr.placeholder]="placeholder"
                [disabled]="isDisabled"
                type="text"
                piTesting="ok-typeahead-text-input"
            />
        </div>

        @if (showDropdown()) {
            <ul #dropdown class="dropdownContent" style="position: absolute">
                @for (item of list; track item; let i = $index) {
                    <li
                        class="test-typeahead-listItem"
                        (mousedown)="itemClicked(item, $event)"
                        (mouseover)="selectedIndex = i"
                        [hidden]="isLoadingPossibilities"
                        [ngClass]="{
                            highlight: selectedIndex === i
                        }"
                    >
                        <ng-template
                            [ngTemplateOutlet]="contentTemplate"
                            [ngTemplateOutletContext]="{
                                item: item
                            }"
                        >
                        </ng-template>
                    </li>
                }
                @if (isLoadingPossibilities) {
                    <li id="test-typeahead-loading">
                        {{ TR.key('loadingellipsis') }}
                    </li>
                }
                @if (showNoResultsMessage()) {
                    <li class="ok-typeahead__noResultsMessage">
                        {{
                            TR.key(
                                'text.common-terms.errormessage.no-results-found'
                            )
                        }}
                    </li>
                }
            </ul>
        }
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: OkTypeAheadComponent,
            multi: true
        }
    ],
    standalone: false
})
export class OkTypeAheadComponent implements ControlValueAccessor {
    // This is the external list of objects that form the data or the dropdown possibilities
    @ViewChild('inputControl', { static: false })
    inputControl: ElementRef;
    @ViewChild('dropdown', { static: false })
    dropdown: ElementRef;
    @Input()
    placeholder = '';

    @Input()
    list: List<TypeaheadItem> = ImmutableList();

    @Input()
    isDisabled = false;
    @Input()
    isLoadingPossibilities = false;
    // Styling specific flags
    @Input()
    searchStyling = false;
    @Input()
    isColorPickerEnabled: boolean;

    @ContentChild(TemplateRef, { static: false })
    contentTemplate: TemplateRef<any>;

    // Events that are emitted
    @Output()
    readonly onSelected = new EventEmitter<any>();
    @Output()
    readonly onTextChange = new EventEmitter<string>();
    @Output()
    readonly onFocusChange = new EventEmitter<boolean>();
    @Output()
    readonly onKeyPress = new EventEmitter<string>();

    selectedIndex = -1;

    hasFocus = false;

    private _boundValue: string;

    // Value needed by ControlValueAccessor - it is a no-op that is replaced by framework
    propagateChange = (_: any) => {
        /* No change propagated */
    };

    constructor(public TR: TranslationService) {}

    get boundValue() {
        return this._boundValue;
    }

    set boundValue(val: string) {
        this._boundValue = val;
        this.propagateChange(this._boundValue);
        this.onTextChange.emit(val);
    }

    showDropdown(): boolean {
        const showDropdown = this.hasFocus;
        return showDropdown;
    }

    showNoResultsMessage(): boolean {
        return (
            !!this.boundValue && !this.list.size && !this.isLoadingPossibilities
        );
    }

    keyPressed(event: KeyboardEvent) {
        this.onKeyPress.emit(event.key);
        switch (event.key) {
            case arrowDownKey:
                this.incSelectedIndex(1);
                break;
            case arrowUpKey:
                this.incSelectedIndex(-1);
                break;
            case enterKey: {
                // there is one // Choose either the one they've selected or the first entry if
                const selected =
                    this.list.get(this.selectedIndex) || this.list.first();
                this.itemClicked(selected, event);
                break;
            }
        }
    }

    focusGained() {
        this.hasFocus = true;
        this.onFocusChange.emit(true);
    }

    focusLost() {
        this.hasFocus = false;
        this.onFocusChange.emit(false);
    }

    itemClicked(item: TypeaheadItem, event: KeyboardEvent) {
        event.preventDefault();
        if (item) {
            this.boundValue = item.text;
            this.inputControl.nativeElement.blur();
            this.onSelected.emit(item);
        }
        this.focusLost();
    }

    /* Meet ControlValueAccessor Interface */
    // Do NOT propagate changes up here
    writeValue(value: string): void {
        this._boundValue = value;
    }

    registerOnChange(fn: any): void {
        this.propagateChange = (changed) => fn.call(this, changed);
    }

    registerOnTouched(): void {
        /* No callback function registered OnTouched */
    }
    /** ***************************************/

    private incSelectedIndex(x: number) {
        if (this.list) {
            this.selectedIndex += x;
            if (this.selectedIndex >= this.list.size) {
                this.selectedIndex = this.list.size - 1;
            }
            if (this.selectedIndex < 0) {
                this.selectedIndex = 0;
            }
        }
    }
}
